// The HTTP layer of the generated API clients (src/api/generated): base URL, session token, JSON and empty
// responses, files, errors, cancellation and a timeout. It knows nothing about the app's state: what happens when
// the session is over is set once with configureTransport().
//
// Session handling is the app's existing one: a missing or expired token ends the session before any request,
// and so do 401 and 406 answers. Nothing is retried automatically.
import { API_BASE_URL } from '../config.js';
import { getCookie } from '../utils/cookies.js';
import { checkJwtTokenValidity } from './session.js';

/** What went wrong with an API call. `kind` tells the UI how to react. */
export class ApiError extends Error {
  /** http: the API answered with an error; session: not signed in; timeout; network: no answer. */
  kind: 'http' | 'session' | 'timeout' | 'network';
  /** The HTTP status, or 0 without an answer. */
  status: number;
  /** The API's error code (saved views: NAME_TAKEN, LIMIT_REACHED, ...), or null. */
  code: string | null;
  /** The problem document (RFC 7807) the API answered with, if any. */
  problem: Record<string, unknown> | null;

  constructor(
    message: string,
    { kind, status = 0, code = null, problem = null }: { kind: ApiError['kind']; status?: number; code?: string | null; problem?: Record<string, unknown> | null },
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.problem = problem;
  }
}

/** Whether an error only means the call was cancelled (a newer request replaced it): not worth showing. */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

const DEFAULT_TIMEOUT_MS = 30_000;

let onSessionEnded: () => void = () => {};

/** Sets what ends the session in the app (logout). Called once at startup. */
export function configureTransport(options: { onSessionEnded?: () => void }) {
  if (options.onSessionEnded) onSessionEnded = options.onSessionEnded;
}

export interface ApiRequestInit extends RequestInit {
  /** Default 30 s. The request is aborted after it (ApiError kind "timeout"). */
  timeoutMs?: number;
  /** How to read a successful answer; default: JSON when it is JSON, else text. */
  responseType?: 'json' | 'blob' | 'text';
}

/** A signal that aborts when the caller's does, or after `timeoutMs`; `timedOut()` tells which one it was. */
function withTimeout(signal: AbortSignal | null | undefined, timeoutMs: number) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort(new DOMException('The request took too long', 'TimeoutError'));
  }, timeoutMs);
  const forward = () => controller.abort(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
  if (signal?.aborted) forward();
  else signal?.addEventListener('abort', forward, { once: true });
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', forward);
    },
  };
}

function isJson(contentType: string | null) {
  return !!contentType && /[/+]json\b/.test(contentType);
}

async function readError(response: Response): Promise<ApiError> {
  let problem: Record<string, unknown> | null = null;
  if (isJson(response.headers.get('Content-Type'))) {
    try {
      problem = await response.json();
    } catch (e) {
      problem = null;
    }
  }
  const code = typeof problem?.code === 'string' ? problem.code : null;
  return new ApiError(`Request failed with status ${response.status}`, { kind: 'http', status: response.status, code, problem });
}

/**
 * Calls the Sensay API. `url` is a path ("/api/v2/recordings?page=0"); a plain object body is sent as JSON,
 * FormData / Blob / ArrayBuffer / string bodies as they are (uploads). Resolves to the parsed answer, or null for
 * an empty one (204). Rejects with ApiError, or with the AbortError when the caller cancelled.
 */
export async function apiFetch<T>(url: string, init: ApiRequestInit = {}): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, responseType, signal, headers, body, ...rest } = init;

  const token = getCookie('jwtToken') || '';
  if (!checkJwtTokenValidity(token)) {
    onSessionEnded();
    throw new ApiError('Invalid token', { kind: 'session' });
  }

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Authorization', 'Bearer ' + token);
  let requestBody = body;
  const plainObject = body !== null && typeof body === 'object' && Object.getPrototypeOf(body) === Object.prototype;
  if (plainObject || Array.isArray(body)) {
    requestBody = JSON.stringify(body);
    requestHeaders.set('Content-Type', 'application/json');
  } else if (typeof body === 'string' && !requestHeaders.has('Content-Type')) {
    // the generated client sends JSON it has already serialized
    requestHeaders.set('Content-Type', 'application/json');
  }

  const timeout = withTimeout(signal, timeoutMs);
  try {
    let response: Response;
    try {
      response = await fetch(API_BASE_URL.replace(/\/$/, '') + url, {
        mode: 'cors',
        cache: 'no-cache',
        referrerPolicy: 'no-referrer',
        ...rest,
        headers: requestHeaders,
        body: requestBody,
        signal: timeout.signal,
      });
    } catch (error) {
      if (timeout.timedOut() || signal?.aborted) throw error;
      throw new ApiError('No answer from the server', { kind: 'network' });
    }

    if (response.status === 401 || response.status === 406) {
      // as every other call of the app: the session is over
      onSessionEnded();
    }
    if (!response.ok) throw await readError(response);

    if (response.status === 204 || response.headers.get('Content-Length') === '0') return null as T;
    if (responseType === 'blob') return (await response.blob()) as T;
    const contentType = response.headers.get('Content-Type');
    if (responseType === 'json' || (responseType === undefined && isJson(contentType))) {
      const text = await response.text();
      return (text ? JSON.parse(text) : null) as T;
    }
    return (await response.text()) as T;
  } catch (error) {
    // the timer (not the caller) aborted the request or the reading of its answer
    if (timeout.timedOut() && !(error instanceof ApiError)) throw new ApiError('The request took too long', { kind: 'timeout' });
    throw error;
  } finally {
    timeout.done();
  }
}
